import React, { Component } from 'react';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { FormattedMessage } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { isUserAuthorized } from '../../util/userHelpers';
import { matchPathname } from '../../util/routes';

import { Modal } from '../../components';

import EmailReminder from './EmailReminder';
import StripeAccountReminder from './StripeAccountReminder';
import css from './ModalMissingInformation.module.css';

const MISSING_INFORMATION_MODAL_WHITELIST = [
  'LoginPage',
  'SignupPage',
  'ContactDetailsPage',
  'EmailVerificationPage',
  'PasswordResetPage',
  'StripePayoutPage',
];

// The Stripe-account reminder is suppressed on the seller's own
// ListingPage too — that page already shows an inline payout-details
// warning, so a duplicate modal would be noisy.
const STRIPE_ACCOUNT_REMINDER_WHITELIST = [
  ...MISSING_INFORMATION_MODAL_WHITELIST,
  'ListingPage',
  'ListingPageVariant',
];

const EMAIL_VERIFICATION = 'EMAIL_VERIFICATION';
const STRIPE_ACCOUNT_NEEDED = 'STRIPE_ACCOUNT_NEEDED';

class ModalMissingInformation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showMissingInformationReminder: null,
      hasSeenMissingInformationReminder: false,
    };
    this.handleMissingInformationReminder = this.handleMissingInformationReminder.bind(this);
  }

  componentDidUpdate() {
    const { currentUser, currentUserHasListings, currentUserHasOrders, location } = this.props;
    const user = ensureCurrentUser(currentUser);
    this.handleMissingInformationReminder(
      user,
      currentUserHasListings,
      currentUserHasOrders,
      location
    );
  }

  handleMissingInformationReminder(
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    newLocation
  ) {
    const routes = this.props.routeConfiguration;
    // Resolve the current pathname to one or more route names. Routes with
    // path params (e.g. ListingPage's `/l/:slug/:id`) can't be reconstructed
    // back into a pathname without the params, so we match the other way:
    // pathname -> route name, then check membership in the whitelist.
    const currentRouteNames = matchPathname(newLocation.pathname, routes).map(m => m.route.name);
    const matchesRouteName = whitelist => currentRouteNames.some(name => whitelist.includes(name));

    // Track if path changes inside Page level component
    const pathChanged = newLocation.pathname !== this.props.location.pathname;
    const notRemindedYet =
      !this.state.showMissingInformationReminder && !this.state.hasSeenMissingInformationReminder;

    // Is the reminder already shown on current page
    const showOnPathChange = notRemindedYet || pathChanged;
    if (!showOnPathChange) {
      return;
    }

    // Emails are sent when order is initiated
    // Customer is likely to get email soon when she books something
    // Provider email should work - she should get an email when someone books a listing
    const hasOrders = currentUserHasOrders === true;
    const hasListingsOrOrders = currentUserHasListings || hasOrders;

    const emailUnverified = !!currentUser.id && !currentUser.attributes.emailVerified;
    const emailVerificationNeeded = hasListingsOrOrders && emailUnverified;

    // Show email reminder first — verifying email is the prerequisite for
    // doing anything else with the account.
    if (emailVerificationNeeded && !matchesRouteName(MISSING_INFORMATION_MODAL_WHITELIST)) {
      this.setState({ showMissingInformationReminder: EMAIL_VERIFICATION });
      return;
    }

    // Sellers can publish listings without connecting Stripe (per
    // marketplace policy), but buyers can't actually purchase from them
    // until payout details are set up. Surface a non-blocking modal so
    // the seller knows their listings aren't yet purchasable.
    const stripeConnected = !!currentUser.id && !!currentUser.attributes?.stripeConnected;
    const stripeAccountNeeded =
      !!currentUser.id && currentUserHasListings && !emailUnverified && !stripeConnected;

    if (stripeAccountNeeded && !matchesRouteName(STRIPE_ACCOUNT_REMINDER_WHITELIST)) {
      this.setState({ showMissingInformationReminder: STRIPE_ACCOUNT_NEEDED });
    }
  }

  render() {
    const {
      rootClassName,
      className,
      containerClassName,
      currentUser,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      onManageDisableScrolling,
      onResendVerificationEmail,
    } = this.props;

    const user = ensureCurrentUser(currentUser);
    const classes = classNames(rootClassName || css.root, className);

    let content = null;

    const currentUserLoaded = user && user.id;
    if (currentUserLoaded && isUserAuthorized(currentUser)) {
      if (this.state.showMissingInformationReminder === EMAIL_VERIFICATION) {
        content = (
          <EmailReminder
            className={classes}
            user={user}
            onResendVerificationEmail={onResendVerificationEmail}
            sendVerificationEmailInProgress={sendVerificationEmailInProgress}
            sendVerificationEmailError={sendVerificationEmailError}
          />
        );
      } else if (this.state.showMissingInformationReminder === STRIPE_ACCOUNT_NEEDED) {
        content = <StripeAccountReminder className={classes} />;
      }
    }

    const closeButtonMessage =
      this.state.showMissingInformationReminder === STRIPE_ACCOUNT_NEEDED ? (
        <FormattedMessage id="ModalMissingInformation.closeStripeAccountReminder" />
      ) : (
        <FormattedMessage id="ModalMissingInformation.closeVerifyEmailReminder" />
      );

    return (
      <Modal
        id="MissingInformationReminder"
        containerClassName={containerClassName}
        isOpen={!!this.state.showMissingInformationReminder}
        onClose={() => {
          this.setState({
            showMissingInformationReminder: null,
            hasSeenMissingInformationReminder: true,
          });
        }}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        closeButtonMessage={closeButtonMessage}
      >
        {content}
      </Modal>
    );
  }
}

/**
 * Modal that tells user that they have not saved all the information the service needs.
 * This is used to remind user that they need to verify their email address.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.containerClassName overwrite components own css.container
 * @param {string} props.id
 * @param {Object} props.currentUser API entity
 * @param {Function} props.onManageDisableScrolling
 * @param {Object?} props.sendVerificationEmailError
 * @param {boolean} props.sendVerificationEmailInProgress
 * @returns {JSX.Element} Modal element if user needs to be reminded
 */
const EnhancedModalMissingInformation = props => {
  const routeConfiguration = useRouteConfiguration();

  return <ModalMissingInformation routeConfiguration={routeConfiguration} {...props} />;
};

export default EnhancedModalMissingInformation;
